import { notEmpty } from "@product/scmp-sdk";
import { useAtomValue } from "jotai";
import type { PageProps } from "next";
import { useRouter } from "next/router";
import React, { useEffect } from "react";
import { graphql, usePreloadedQuery } from "react-relay";
import type { RelayComponent } from "relay-nextjs";
import { withRelay } from "relay-nextjs";

import { AppFooter } from "~/components/app-footer";
import { FallbackLoading } from "~/components/fallback-loading";
import { HighlightBlock } from "~/components/plus/home/highlight-block";
import { MoreOnTopicBlock } from "~/components/plus/home/more-on-topic-block";
import { PlusMobileScreen } from "~/components/plus/mobile-screen";
import { PlusHomeSeo } from "~/components/plus/plus-home-seo";
import { PlusWelcomePopup } from "~/components/plus/welcome-popup";
import { trackingAtom } from "~/components/tracking";
import { article } from "~/data";
import { createClientEnvironment } from "~/lib/relay/environment.client";
import { isString } from "~/lib/utils";
import { useGetPageLocationForPlusPage } from "~/pages/plus/hooks";
import type { plusPageQuery } from "~/queries/__generated__/plusPageQuery.graphql";

import {
  Container,
  FirstRow,
  HighlightFromSCMPPlus,
  MoreOnTopic,
  plusGlobalCss,
  SecondRow,
  StyledAgendaBlock,
  StyledDailyPulseBlock,
  Wrapper,
} from "./styles";

export const query = graphql`
  query plusPageQuery(
    $dailyPulseTypeUuid: String!
    $highlightQueueName: String!
    $nodeId: ID!
    $hasNodeId: Boolean!
  ) {
    node(id: $nodeId) @include(if: $hasNodeId) {
      ...plusHomeSeoNode
    }
    ...dailyPulseBlockQuery @arguments(dailyPulseTypeUuid: $dailyPulseTypeUuid)
    ...highlightBlockQuery @arguments(queueName: $highlightQueueName)
    ...moreOnTopicBlockQuery
      @arguments(dailyPulseTypeUuid: $dailyPulseTypeUuid, queueName: $highlightQueueName)
  }
`;

const PlusPage: RelayComponent<PageProps, plusPageQuery> = ({ preloadedQuery }) => {
  const data = usePreloadedQuery(query, preloadedQuery);
  const pageLocation = useGetPageLocationForPlusPage();

  const router = useRouter();
  const { trackPageView } = useAtomValue(trackingAtom);

  useEffect(() => {
    const articleId = router.query.articleId;
    trackPageView({
      articleID: articleId,
      entityId: articleId,
      page_location: pageLocation,
      product: "plus",
    });
    // article_completion_sys get the articleId from the page.articleID
    // so insert articleID through trackPageView function into dataLayer variable
    // but page_location also need to be inserted into the page params
    // if add both into the dependency array, it will trigger the useEffect
    // twice every page changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageLocation, trackPageView]);

  return (
    <>
      <PlusHomeSeo reference={data.node} />
      <Container>
        <PlusMobileScreen />
        <PlusWelcomePopup />
        <Wrapper>
          <FirstRow>
            <StyledDailyPulseBlock reference={data} />
            <StyledAgendaBlock />
          </FirstRow>

          <SecondRow>
            <MoreOnTopic>
              <MoreOnTopicBlock reference={data} />
            </MoreOnTopic>

            <HighlightFromSCMPPlus>
              <HighlightBlock reference={data} />
            </HighlightFromSCMPPlus>
          </SecondRow>
        </Wrapper>
      </Container>

      <AppFooter variant="plus-with-app-link-row" />
    </>
  );
};

PlusPage.displayName = "Plus";

const pageProps: PageProps = {
  animateKey: "plus-home",
  appBarConfiguration: {
    variant: "scmp/plus",
  },
  appInitializerConfiguration: {
    application: "plus",
  },
  extraGlobalCss: plusGlobalCss,
};

export default withRelay(PlusPage, query, {
  clientSideProps: () => pageProps,
  createClientEnvironment: () => createClientEnvironment(),
  createServerEnvironment: async context => {
    const { createServerEnvironment } = await import("~/lib/relay/environment.server");
    return createServerEnvironment(context.req?.cookies);
  },
  fallback: <FallbackLoading />,
  serverSideProps: () => Promise.resolve(pageProps),
  variablesFromContext(context) {
    return {
      dailyPulseTypeUuid: article.types.dailyPulseBriefing.entityUuid,
      hasNodeId: notEmpty(context.query?.nodeId),
      highlightQueueName: "scmp_plus_home_news",
      nodeId: isString(context.query.nodeId) ? context.query?.nodeId ?? "" : "",
    };
  },
});
